'use client';

import { type FC, type ReactNode, useCallback } from 'react';
import { cn } from '@/app/utils';

export interface SliderFieldProps {
  label: ReactNode;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
  /** Right-aligned content next to the label (e.g. a Switch). */
  headerRight?: ReactNode;
  /** When false, hides the slider track but keeps the label row visible. */
  showSlider?: boolean;
  className?: string;
}

/**
 * 通用滑块表单项：上方 label + 可选 headerRight，下方滑块带数值显示。
 * 视觉与对口型 / TTS 等工具设置的滑块保持一致，可在表单与右侧属性面板复用。
 */
export const SliderField: FC<SliderFieldProps> = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  formatValue,
  headerRight,
  showSlider = true,
  className,
}) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(Number(e.target.value));
    },
    [onChange],
  );

  const range = max - min;
  const percentage = range > 0 ? ((value - min) / range) * 100 : 0;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-system-white">{label}</span>
        {headerRight}
      </div>
      {showSlider && (
        <div className="relative flex h-11 items-center rounded-12 border border-system-white08 bg-system-white04 px-4 transition-colors hover:bg-system-white08">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={handleChange}
            className={cn(
              'h-1.5 w-full cursor-pointer appearance-none rounded-full bg-system-white08 outline-none',
              '[&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-system-white [&::-webkit-slider-thumb]:shadow-md',
              '[&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:bg-system-white [&::-moz-range-thumb]:shadow-md',
            )}
            style={{
              background: `linear-gradient(to right, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.6) ${percentage}%, rgba(255,255,255,0.08) ${percentage}%, rgba(255,255,255,0.08) 100%)`,
            }}
          />
          <span className="ml-3 shrink-0 text-sm text-system-white/60">{formatValue ? formatValue(value) : value}</span>
        </div>
      )}
    </div>
  );
};
